import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, debounceTime, forkJoin, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { ContainerTypesService } from '@core/container-types/container-types.service';
import { capacityCellsForDryType } from '@core/offers/offer-containers';
import { legacyVolumeM3 } from '@core/proposals/pricing';
import { SpeciesCapacityService } from '@core/species/species-capacity.service';
import { backendErrorMessage } from '@core/proposals/backend-error';
import {
  buildContainerFreightOption,
  ContainerFreightOptionView,
  defaultDestinationPortId,
  FreightDestinationPort,
  freightQuery,
  mayHaveMoreFreights,
  legacyFreightValuePerM3,
  LineContainer,
  LineForFreight,
  mainPrice,
  resolveCostContainerTypeId,
  resolveFreightContainerTypeId,
  todayISO,
} from '@core/proposals/freight-options';
import { ProposalService } from '@core/proposals/proposal.service';
import { ShippingService } from '@core/shipping/shipping.service';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'app-dialog-add-shipping',
  templateUrl: './dialog-add-shipping.component.html',
  styleUrls: ['./dialog-add-shipping.component.scss'],
})
export class DialogAddShippingComponent implements OnInit, OnDestroy {
  private readonly _unsubscribeAll = new Subject<void>();
  private readonly _search$ = new Subject<string>();
  /** Free text over the freight list (company, destination, type, notes, continent); '' = no search. */
  searchText = '';

  proposal: any;
  product: any;
  /** The proposal line (ProductOffer2Proposal) with volumeM3, containers snapshot and additionalCosts. */
  line: LineForFreight | null;
  productOffer2ProposalId: number;
  existingCostId: number;
  /** Container line: the container type of the cost being changed. null for non container lines (manual flow). */
  container: LineContainer | null = null;
  options: any[] = [];
  /** Container line only: capacity, per m3 contribution for this line and expiry of each option (same order as `options`). */
  views: ContainerFreightOptionView[] = [];
  selectedId: number | null = null;
  saving = false;
  errorMessage: string | null = null;
  /** Destination ports with freights from the origin port; the select shows them with "Todos" (null). */
  destinationOptions: FreightDestinationPort[] = [];
  /** Destination port filter of the freight list; null = "Todos". */
  destinyPortId: number | null = null;
  /** The last freight query filled the take. */
  moreFreights = false;
  private originPortId: number;

  constructor(
    private _dialogRef: MatDialogRef<DialogAddShippingComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _shippingService: ShippingService,
    private _proposalService: ProposalService,
    private _translocoService: TranslocoService,
    private _containerTypesService: ContainerTypesService,
    private _speciesCapacityService: SpeciesCapacityService,
  ) {
    this.proposal = data.proposal;
    this.product = data.product;
    this.line = data.line ?? null;
    this.productOffer2ProposalId = data.productOffer2ProposalId;
    this.existingCostId = data.existingCostId;
    this.container = this.findContainer();
  }

  /** A bulk line takes BULK freights only, quoted per m3 and used as is; it is never moved to containers. */
  get isBulkLine(): boolean {
    return this.line?.transportMode === 'BULK';
  }

  private findContainer(): LineContainer | null {
    const containers = this.line?.containers ?? [];
    if (containers.length === 0) {
      return null;
    }
    const cost = (this.line?.additionalCosts ?? []).find((c) => c.id === this.existingCostId) ?? null;
    const typeId = resolveCostContainerTypeId(this.line, cost);
    return containers.find((c) => c.quantityTypeId === typeId) ?? containers[0];
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  ngOnInit(): void {
    this.originPortId = Number(this.product.originPort.id);
    this._search$.pipe(debounceTime(300), takeUntil(this._unsubscribeAll)).subscribe((text) => {
      this.searchText = text.trim();
      this.loadFreights();
    });

    if (!this.container) {
      this._shippingService.shippings$.subscribe((options) => (this.options = options ?? []));
    }

    // The default destination (request port, else the only client port) needs the options first; a failing
    // destination query keeps "Todos" and never blocks the freight list.
    this._shippingService
      .getDestinationPorts(this.originPortId, this.container?.quantityTypeId)
      .pipe(take(1))
      .subscribe({
        next: (options) => {
          this.destinationOptions = (options ?? []).filter((o) => o.portId != null);
          this.destinyPortId = defaultDestinationPortId(
            this.destinationOptions,
            this.data.requestDestinyPortId,
            this.data.clientPortIds,
          );
          this.loadFreights();
        },
        error: () => this.loadFreights(),
      });
  }

  /** Types in the freight search box: re-queries once the user pauses typing. */
  changeSearch(text: string): void {
    this._search$.next(text ?? '');
  }

  /** Re-queries the freight list for another destination port (null = "Todos"). */
  changeDestination(portId: number | null): void {
    this.destinyPortId = portId == null ? null : +portId;
    this.loadFreights();
  }

  private loadFreights(): void {
    if (this.container) {
      const containers = this.line.containers;
      this._shippingService
        .getList(
          freightQuery({
            originPortId: this.originPortId,
            quantityTypeId: this.container.quantityTypeId,
            destinyPortId: this.destinyPortId,
            search: this.searchText,
          }),
        )
        .pipe(take(1))
        .subscribe({
          next: (rows) => {
            const today = todayISO();
            this.options = rows ?? [];
            this.views = this.options.map((freight) =>
              buildContainerFreightOption(freight, this.container, containers, this.line.volumeM3 ?? 0, today),
            );
            this.moreFreights = mayHaveMoreFreights(rows);
          },
          error: () => {
            this.errorMessage = this._translocoService.translate('proposals.dialogs.addShipping.loadFreightError');
          },
        });
      return;
    }

    this._shippingService
      .getList(
        freightQuery({
          originPortId: this.originPortId,
          destinyPortId: this.destinyPortId,
          search: this.searchText,
          bulkOnly: this.isBulkLine,
        }),
      )
      .pipe(take(1))
      .subscribe((rows) => (this.moreFreights = mayHaveMoreFreights(rows)));
  }

  close(): void {
    this._dialogRef.close();
  }

  onSelect(shipping: any): void {
    this.selectedId = shipping.id;
    this.saving = true;
    this.errorMessage = null;

    // Container line: one AdditionalCostUpdate (the backend replaces the basis and re-derives the value).
    // Non container line: when the freight type resolves to a container type with a known capacity, the line is moved
    // to containers (count = ceil(offer quantity / capacity), editable afterwards in the line) and the freight is
    // written per container. A freight type NOT linked to a container type is added without value (pending container type,
    // chosen later on the line), and so is one whose capacity is missing (F2); only an offer without species/dry type/quantity
    // keeps the manual EUR/m3 flow (remove + add), TODO(OQ5): LCL/BULK tariff unit unknown.
    const write$ = this.container
      ? this._proposalService.updateAdditionalCostFreight(this.existingCostId, shipping.id)
      : this.resolveLegacyContainerType(shipping).pipe(
          switchMap((resolved) => {
            const pendingType = resolved === 'pending';
            const typeId = pendingType ? null : (resolved as number | null);
            return this._proposalService.removeAdditionalCostFromOffer(this.existingCostId).pipe(
              switchMap(() =>
                typeId != null
                  ? this._proposalService.setLineContainerType(+this.productOffer2ProposalId, typeId)
                  : of(null),
              ),
              switchMap(() =>
                this._proposalService.addAdditionalCostToOffer(
                  this.productOffer2ProposalId,
                  typeId != null || pendingType
                    ? { type: 'frete', description: this._translocoService.translate('proposals.freight.description'), freightId: shipping.id }
                    : {
                        type: 'frete',
                        description: this._translocoService.translate('proposals.freight.description'),
                        value: legacyFreightValuePerM3(mainPrice(shipping).value, this.line?.volumeM3 ?? 0, shipping),
                        freightId: shipping.id,
                      },
                ),
              ),
            );
          }),
        );

    write$.subscribe({
      next: () => this.refreshAndClose(),
      error: (err) => {
        this.saving = false;
        this.selectedId = null;
        this.errorMessage =
          backendErrorMessage(err) ?? this._translocoService.translate('proposals.dialogs.addShipping.saveError');
      },
    });
  }

  /**
   * Container type to move a legacy line to, 'pending' when the freight type is not linked to a container type (the freight
   * is added without value) or its capacity is missing, or null for the manual flow (only when the offer has no species,
   * dry type or quantity). Resolved BEFORE the old manual cost is removed.
   */
  private resolveLegacyContainerType(shipping: any) {
    const specyId = Number(this.product?.specy?.id);
    const dryTypeId = Number(this.product?.dryTypeId ?? this.product?.dryType?.id);
    const volume = legacyVolumeM3(this.product?.quantity);
    if (this.isBulkLine || !(specyId > 0) || !(dryTypeId > 0) || !(volume > 0)) {
      return of(null as number | 'pending' | null);
    }
    return forkJoin([
      this._containerTypesService.getList().pipe(take(1)),
      this._speciesCapacityService.getGrid(specyId).pipe(take(1)),
    ]).pipe(
      switchMap(([types, grid]) => {
        const typeId = resolveFreightContainerTypeId(
          shipping,
          (types ?? []).filter((t) => t.id != null).map((t) => +t.id),
        );
        const cell = typeId == null ? null : capacityCellsForDryType(grid, dryTypeId).find((c) => +c.quantityTypeId === typeId);
        if (typeId == null) {
          return of('pending' as const);
        }
        // F2: capacity missing for the species/dry type in that container: never a manual EUR/m3, the freight is
        // added pending and the line asks for the container type (and says the capacity is missing).
        return of(cell?.capacityM3 != null && cell.capacityM3 > 0 ? typeId : ('pending' as const));
      }),
      // Types or grid unavailable: pending as well (the line resolves it), never a silent EUR/m3 price.
      catchError(() => of('pending' as number | 'pending' | null)),
    );
  }

  private refreshAndClose(): void {
    this._proposalService.getByIdToGetProducts(this.proposal.id).subscribe({
      next: (res) => {
        this.saving = false;
        if (res?.ProductOffer2Proposal) {
          res.ProductOffer2Proposal.forEach((element: any) => {
            this._proposalService.products = element;
          });
        }
        // true tells DetailComponent.changeFreight to notify and refresh the proposal total.
        this._dialogRef.close(true);
      },
      error: () => {
        this.saving = false;
        this.errorMessage = this._translocoService.translate('proposals.dialogs.addShipping.refreshError');
      },
    });
  }
}
